import { useState, useEffect, createContext, useContext } from 'react';
import { toast } from 'sonner';
import { ShopifyProduct, ShopifyVariant } from '@/hooks/useShopify';
import { productionLogger } from '@/utils';

export interface ShopifyCartItem {
  product: ShopifyProduct;
  variant: ShopifyVariant;
  quantity: number;
  originalPrice?: number;
}

interface ShopifyCartContextType {
  items: ShopifyCartItem[];
  addToCart: (product: ShopifyProduct, variant: ShopifyVariant, quantity?: number) => void;
  removeFromCart: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getTotalSavings: () => number;
  isInCart: (variantId: string) => boolean;
  getCheckoutUrl: () => string;
}

const ShopifyCartContext = createContext<ShopifyCartContextType | undefined>(undefined);

export const useShopifyCart = () => {
  const context = useContext(ShopifyCartContext);
  if (!context) {
    throw new Error('useShopifyCart must be used within a ShopifyCartProvider');
  }
  return context;
};

interface ShopifyCartProviderProps {
  children: React.ReactNode;
}

export const ShopifyCartProvider: React.FC<ShopifyCartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<ShopifyCartItem[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    
    const savedCart = localStorage.getItem('shopify-cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (error) {
        productionLogger.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    localStorage.setItem('shopify-cart', JSON.stringify(items));
  }, [items]);

  const addToCart = (product: ShopifyProduct, variant: ShopifyVariant, quantity: number = 1) => {
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(item => item.variant.id === variant.id);
      
      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        return updatedItems;
      } else {
        // Calculate discounted price if bundle discount applies
        const finalPrice = parseFloat(variant.price.amount);
        
        // Add new item with bundle discount applied
        const newItem: ShopifyCartItem = { 
          product, 
          variant: {
            ...variant,
            price: {
              ...variant.price,
              amount: finalPrice.toFixed(2)
            }
          }, 
          quantity,
        };
        return [...prevItems, newItem];
      }
    });

    // Create detailed toast message with product title, quantity, and variant options
    const variantOptions = variant.selectedOptions
      ?.filter(option => option.value && option.value.toLowerCase() !== 'default title')
      .map(option => option.value)
      .join('/') || '';
    
    const variantText = variantOptions ? ` in ${variantOptions}` : '';
    
    toast.success("Added to cart", {
      description: `A quantity of ${quantity} ${product.title}${variantText} has been added to your cart.`,
    });
  };

  const removeFromCart = (variantId: string) => {
    setItems(prevItems => prevItems.filter(item => item.variant.id !== variantId));
    
    toast.success("Removed from cart", {
      description: "Item has been removed from your cart.",
    });
  };

  const updateQuantity = (variantId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(variantId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.variant.id === variantId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    toast.success("Cart cleared", {
      description: "All items have been removed from your cart.",
    });
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.variant.price.amount);
      return total + (price * item.quantity);
    }, 0);
  };

  const getTotalSavings = () => {
    return items.reduce((total, item) => {
      let savings = 0;
      
      // Check for bundle/original price savings
      if (item.originalPrice) {
        const currentPrice = parseFloat(item.variant.price.amount);
        savings += (item.originalPrice - currentPrice) * item.quantity;
      }
      
      // Check for Shopify compareAt price savings  
      if (item.variant.compareAtPrice?.amount) {
        const compareAtPrice = parseFloat(item.variant.compareAtPrice.amount);
        const currentPrice = parseFloat(item.variant.price.amount);
        if (compareAtPrice > currentPrice) {
          savings += (compareAtPrice - currentPrice) * item.quantity;
        }
      }
      
      return total + savings;
    }, 0);
  };

  const isInCart = (variantId: string) => {
    return items.some(item => item.variant.id === variantId);
  };


  const getCheckoutUrl = () => {
    if (items.length === 0) {
      return '#';
    }

    const cartItems = items.map(item => ({
      variantId: item.variant.id.replace('gid://shopify/ProductVariant/', ''),
      quantity: item.quantity
    }));

    const cartParams = cartItems
      .map(item => `${item.variantId}:${item.quantity}`)
      .join(',');
    
    // Generate unique checkout ID for tracking
    const checkoutId = `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Build return URL with success parameters
    const returnUrl = encodeURIComponent(
      `${window.location.origin}/?checkout_success=true&checkout_id=${checkoutId}`
    );
    
    return `https://shop.bohostyleco.com/cart/${cartParams}?return_to=${returnUrl}`;
  };

  const value: ShopifyCartContextType = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getTotalSavings,
    isInCart,
    getCheckoutUrl,
  };

  return (
    <ShopifyCartContext.Provider value={value}>
      {children}
    </ShopifyCartContext.Provider>
  );
};